import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import ProductCard from "../components/store/ProductCard";
import { SlidersHorizontal } from "lucide-react";

const Shop = () => {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";

  useEffect(() => { apiClient.get("/categories").then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const query = {
      sort,
      ...(category ? { category } : {}),
      ...(q ? { q } : {}),
      ...(minPrice ? { min_price: minPrice } : {}),
      ...(maxPrice ? { max_price: maxPrice } : {}),
    };
    apiClient.get("/products", { params: query }).then((r) => {
      setItems(r.data.items);
      setTotal(r.data.total);
    }).finally(() => setLoading(false));
  }, [category, q, sort, minPrice, maxPrice]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const heading = useMemo(() => {
    if (q) return `"${q}"`;
    if (category) {
      const c = categories.find((x) => x.slug === category);
      return c ? (lang === "nl" ? c.name_nl : c.name_en) : t("nav_shop");
    }
    return t("nav_shop");
  }, [q, category, categories, lang, t]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10" data-testid="shop-page">
      <div className="flex items-end justify-between border-b border-black/5 pb-6 mb-8">
        <div>
          <div className="font-label text-ink-muted">{total} {lang === "nl" ? "items" : "items"}</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-light mt-2">{heading}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="filters-toggle" onClick={() => setFiltersOpen(!filtersOpen)} className="md:hidden btn-secondary text-xs py-2 px-3"><SlidersHorizontal className="w-3.5 h-3.5" /> Filter</button>
          <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="border border-black/10 px-3 py-2 text-sm bg-white">
            <option value="newest">{t("sort_newest")}</option>
            <option value="price_asc">{t("sort_price_asc")}</option>
            <option value="price_desc">{t("sort_price_desc")}</option>
            <option value="rating">{t("sort_rating")}</option>
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-10">
        <aside className={`md:col-span-3 ${filtersOpen ? "block" : "hidden md:block"}`} data-testid="shop-filters">
          <div className="font-label text-ink-muted mb-3">{t("filter_category")}</div>
          <ul className="space-y-2 mb-8">
            <li><button onClick={() => setParam("category", "")} className={`text-sm hover:text-terracotta ${!category ? "text-terracotta font-medium" : ""}`}>{lang === "nl" ? "Alle" : "All"}</button></li>
            {categories.map((c) => (
              <li key={c.slug}>
                <button data-testid={`filter-category-${c.slug}`} onClick={() => setParam("category", c.slug)} className={`text-sm hover:text-terracotta ${category === c.slug ? "text-terracotta font-medium" : ""}`}>
                  {lang === "nl" ? c.name_nl : c.name_en}
                </button>
              </li>
            ))}
          </ul>
          <div className="font-label text-ink-muted mb-3">{t("filter_price")}</div>
          <div className="flex gap-2">
            <input data-testid="filter-min-price" type="number" placeholder="€ min" defaultValue={minPrice} onBlur={(e) => setParam("min_price", e.target.value)} className="w-24 border border-black/10 px-2 py-2 text-sm" />
            <input data-testid="filter-max-price" type="number" placeholder="€ max" defaultValue={maxPrice} onBlur={(e) => setParam("max_price", e.target.value)} className="w-24 border border-black/10 px-2 py-2 text-sm" />
          </div>
        </aside>

        <div className="md:col-span-9">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-secondary-warm animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center text-ink-muted">
              {lang === "nl" ? "Geen producten gevonden." : "No products found."}
              <div className="mt-4"><Link to="/shop" className="btn-secondary text-xs">{t("continue_shopping")}</Link></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-12">
              {items.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
